// =============================================================
// SCREENS 4 — Clash · Scouting d'équipe
//  · Retrouve l'équipe Clash d'un joueur (clash-v1) ou scoute une liste
//    de Riot IDs relevés dans le lobby adverse.
//  · Rapport par joueur (rang, pool, rôles, forme, historique Clash)
//    + suggestions de bans agrégées côté API.
// =============================================================
import React, { useState, useMemo } from 'react';
import { SecHead, Spinner, ErrorState, EmptyState } from './components';
import { getChamp, champSquare, champLoading, profileIcon } from './ddragon';
import { useApi } from './useApi';
import { btrApi } from './api';
import { tierFr, laneLabel } from './format';

const POSITION_ORDER = { TOP: 0, JUNGLE: 1, MIDDLE: 2, BOTTOM: 3, UTILITY: 4, FILL: 5, UNSELECTED: 6 };

// Champion warehouse/Riot ("MonkeyKing", id 62…) -> fiche ddragon (nom FR + image)
function champInfo(entry) {
  const c = getChamp(entry.championId) || getChamp(entry.name) || null;
  return { id: c?.id || entry.name, label: c?.name || entry.name };
}

function loadTarget() {
  try { return JSON.parse(localStorage.getItem('btr-clash-target')) || null; } catch { return null; }
}

function ClashScreen({ region = 'EUW' }) {
  const [mode, setMode] = useState('team'); // team | lobby
  const [riotId, setRiotId] = useState('');
  const [lobby, setLobby] = useState('');
  const [target, setTarget] = useState(loadTarget);

  const tourn = useApi(`clash-tournaments-${region}`, () => btrApi.clashTournaments({ region }), { ttl: 10 * 60_000 });

  const key = target
    ? `clash-scout-${target.region}-${target.mode}-${target.mode === 'team' ? target.riotId : target.riotIds}`
    : null;
  const scout = useApi(key, () => btrApi.clashScout(
    target.mode === 'team'
      ? { region: target.region, riotId: target.riotId }
      : { region: target.region, riotIds: target.riotIds },
  ), { ttl: 10 * 60_000 });

  const submit = (e) => {
    e.preventDefault();
    let next = null;
    if (mode === 'team') {
      const t = riotId.trim();
      if (!t.includes('#')) return;
      next = { mode: 'team', region, riotId: t };
    } else {
      const ids = lobby.split(/[,\n]/).map((s) => s.trim()).filter((s) => s.includes('#')).slice(0, 5);
      if (!ids.length) return;
      next = { mode: 'lobby', region, riotIds: ids.join(',') };
    }
    setTarget(next);
    try { localStorage.setItem('btr-clash-target', JSON.stringify(next)); } catch { /* nav privée */ }
  };

  const data = scout.data;
  const players = useMemo(() => (data?.players
    ? [...data.players].sort((a, b) => (POSITION_ORDER[a.position] ?? 9) - (POSITION_ORDER[b.position] ?? 9))
    : []), [data]);

  return (
    <div className="container anim-fade-in">
      <header style={{ marginBottom: 28 }}>
        <div className="eyebrow">Clash · Scouting</div>
        <h1 className="h1" style={{ marginTop: 10 }}>
          Connais tes <span style={{ color: 'var(--accent)' }}>adversaires</span> avant la draft.
        </h1>
        <p className="lead" style={{ marginTop: 14, maxWidth: 720 }}>
          Analyse une équipe Clash complète : rangs, pools de champions, rôles, forme récente
          et historique Clash de chaque joueur — avec des suggestions de bans prêtes pour la draft.
        </p>
      </header>

      <ClashCalendar state={tourn} />

      <div className="card" style={{ padding: 20, marginBottom: 28 }}>
        <div className="row-bw" style={{ flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
          <div className="seg">
            <button className={mode === 'team' ? 'active' : ''} onClick={() => setMode('team')}>Via un joueur inscrit</button>
            <button className={mode === 'lobby' ? 'active' : ''} onClick={() => setMode('lobby')}>Lobby adverse (manuel)</button>
          </div>
          <span className="tag accent" title="Région choisie dans la barre du haut">{region}</span>
        </div>

        <form onSubmit={submit}>
          {mode === 'team' ? (
            <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <input value={riotId} onChange={(e) => setRiotId(e.target.value)} placeholder="Riot ID d'un membre de l'équipe (Nom#TAG)…"
                     style={{ flex: 1, minWidth: 240, background: 'var(--surface-2)', border: '1px solid var(--border)',
                              borderRadius: 10, padding: '10px 14px', color: 'var(--ink)', fontSize: 14, outline: 'none' }}/>
              <button className="btn primary" disabled={!riotId.includes('#')}>Scouter son équipe</button>
            </div>
          ) : (
            <>
              <textarea value={lobby} onChange={(e) => setLobby(e.target.value)} rows={5}
                        placeholder={"Un Riot ID par ligne (jusqu'à 5), relevés dans le lobby :\nNom1#TAG\nNom2#TAG\n…"}
                        style={{ width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border)',
                                 borderRadius: 10, padding: '10px 14px', color: 'var(--ink)', fontSize: 14,
                                 outline: 'none', resize: 'vertical', fontFamily: 'var(--font-body)' }}/>
              <div className="row-bw" style={{ marginTop: 12 }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  Astuce : en salle d'attente Clash, les 5 pseudos adverses sont visibles avant la draft.
                </span>
                <button className="btn primary" disabled={!lobby.includes('#')}>Scouter ce lobby</button>
              </div>
            </>
          )}
        </form>
      </div>

      {target && scout.loading && !data && (
        <Spinner label="Scouting en cours — analyse des 5 joueurs via Riot…" />
      )}
      {target && scout.error && (
        scout.error.status === 404 || /aucune équipe|inscrit/i.test(String(scout.error.message))
          ? <EmptyState icon="user" title="Pas d'équipe Clash trouvée" hint={String(scout.error.message)} />
          : <ErrorState error={scout.error} onRetry={scout.refetch} />
      )}
      {!target && (
        <EmptyState icon="search" title="Aucune équipe scoutée"
                    hint="Entre le Riot ID d'un joueur inscrit en Clash, ou colle les 5 pseudos du lobby adverse." />
      )}

      {data && (
        <>
          {data.team && (
            <section className="card" style={{ padding: 24, marginBottom: 28 }}>
              <div className="row-bw" style={{ flexWrap: 'wrap', gap: 14 }}>
                <div>
                  <div className="eyebrow">Équipe Clash</div>
                  <div className="h2" style={{ marginTop: 6 }}>
                    {data.team.name} <span className="muted" style={{ fontWeight: 500 }}>[{data.team.abbreviation}]</span>
                  </div>
                </div>
                <span className="tag accent" style={{ fontSize: 13 }}>Tier {data.team.tier}</span>
              </div>
            </section>
          )}

          <SecHead title="Les cinq joueurs" meta="données Riot + warehouse · rapport mis en cache 10 min" />
          <div className="clash-grid stagger" style={{ marginBottom: 40 }}>
            {players.map((p) => <PlayerScoutCard key={p.puuid} p={p} />)}
          </div>

          <BanBoard bans={data.bans} />
        </>
      )}

      <style>{`
        .clash-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
      `}</style>
    </div>
  );
}

// ---------- Calendrier des prochains tournois ----------
function ClashCalendar({ state }) {
  const list = Array.isArray(state.data) ? state.data : [];
  const upcoming = list
    .flatMap((t) => (t.schedule || []).filter((s) => !s.cancelled).map((s) => ({ ...s, nameKey: t.nameKey, nameKeySecondary: t.nameKeySecondary })))
    .filter((s) => s.startTime > Date.now() - 12 * 3600_000)
    .sort((a, b) => a.startTime - b.startTime)
    .slice(0, 3);
  if (state.loading && !list.length) return null;
  return (
    <section className="card" style={{ padding: 18, marginBottom: 28, borderLeft: '3px solid var(--accent)' }}>
      <div className="row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span className="eyebrow" style={{ margin: 0 }}>Prochains Clash</span>
        {upcoming.length === 0 ? (
          <span className="muted" style={{ fontSize: 13 }}>Aucun tournoi annoncé sur cette région pour l'instant.</span>
        ) : upcoming.map((s) => (
          <span key={s.id} className="tag" style={{ fontSize: 12 }}>
            {formatTournName(s.nameKey, s.nameKeySecondary)} · {new Date(s.startTime).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
            {' '}{new Date(s.startTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        ))}
      </div>
    </section>
  );
}

const formatTournName = (key, sec) => {
  const pretty = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return pretty(key) + (sec ? ` — ${pretty(sec)}` : '');
};

// ---------- Carte joueur ----------
function PlayerScoutCard({ p }) {
  const solo = p.ranks.find((r) => r.queueType === 'RANKED_SOLO_5x5');
  const flex = p.ranks.find((r) => r.queueType === 'RANKED_FLEX_SR');
  const mainRole = p.position && p.position !== 'FILL' && p.position !== 'UNSELECTED'
    ? p.position : p.roles[0]?.role;
  return (
    <div className="card" style={{ padding: 18 }}>
      <div className="row" style={{ gap: 12, marginBottom: 12 }}>
        {p.profileIconId != null && (
          <img src={profileIcon(p.profileIconId)} alt="" width={44} height={44}
               style={{ borderRadius: 10, border: '1px solid var(--border)' }}/>
        )}
        <div className="grow">
          <div style={{ fontWeight: 800, fontSize: 15 }}>{p.riotId || '(inconnu)'}</div>
          <div className="row-tight" style={{ gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <span className="tag accent" style={{ fontSize: 11 }}>{laneLabel(mainRole)}</span>
            {p.position === 'FILL' && <span className="tag" style={{ fontSize: 11 }}>Fill</span>}
            {p.level != null && <span className="tag" style={{ fontSize: 11 }}>Niv. {p.level}</span>}
          </div>
        </div>
      </div>

      <div className="row-tight" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        <RankTag label="Solo/Duo" rank={solo} />
        <RankTag label="Flex" rank={flex} />
      </div>

      {/* Forme récente */}
      {p.form.length > 0 && (
        <div className="row-tight" style={{ gap: 4, marginBottom: 12, alignItems: 'center' }}>
          <span className="muted" style={{ fontSize: 11, marginRight: 4 }}>Forme</span>
          {p.form.map((w, i) => (
            <span key={i} title={w ? 'Victoire' : 'Défaite'}
                  style={{ width: 9, height: 9, borderRadius: '50%', background: w ? 'var(--win)' : 'var(--loss)', opacity: 1 - i * 0.05 }}/>
          ))}
        </div>
      )}

      {/* Pool de champions */}
      <div className="muted" style={{ fontSize: 11, marginBottom: 6 }}>
        Pool de champions {p.poolSource === 'warehouse' ? '(historique complet)' : '(parties récentes)'}
      </div>
      <div className="row-tight" style={{ gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {p.pool.length === 0 ? <span className="muted" style={{ fontSize: 12 }}>Aucune donnée</span>
          : p.pool.slice(0, 6).map((c) => {
            const info = champInfo(c);
            return (
              <div key={info.id} title={`${info.label} — ${c.games} parties · ${c.winrate}% WR${c.kda ? ` · ${c.kda} KDA` : ''}`}
                   style={{ textAlign: 'center', width: 46 }}>
                <img src={champSquare(info.id)} alt={info.label} width={40} height={40} loading="lazy"
                     style={{ borderRadius: 8, border: `2px solid ${c.winrate >= 55 ? 'var(--loss)' : 'var(--border)'}` }}/>
                <div className="mono" style={{ fontSize: 10, color: c.winrate >= 55 ? 'var(--loss)' : 'var(--ink-3)', marginTop: 2 }}>
                  {c.games}p·{Math.round(c.winrate)}%
                </div>
              </div>
            );
          })}
      </div>

      {/* Historique Clash */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <span className="muted" style={{ fontSize: 11 }}>Clash récent : </span>
        {p.clash.games === 0 ? (
          <span className="muted" style={{ fontSize: 12 }}>aucune partie trouvée</span>
        ) : (
          <span style={{ fontSize: 12, fontWeight: 700 }}>
            {p.clash.wins}V / {p.clash.games - p.clash.wins}D
            <span className="muted" style={{ fontWeight: 500 }}>
              {' '}· {p.clash.champions.slice(0, 3).map((c) => champInfo(c).label).join(', ')}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function RankTag({ label, rank }) {
  if (!rank) return <span className="tag" style={{ fontSize: 11, opacity: 0.6 }}>{label} : non classé</span>;
  return (
    <span className="tag" style={{ fontSize: 11 }}>
      {label} : <strong style={{ marginLeft: 4 }}>{tierFr(rank.tier)} {rank.rank}</strong>
      <span className="muted" style={{ marginLeft: 4 }}>{rank.lp} LP · {rank.winrate}%</span>
    </span>
  );
}

// ---------- Suggestions de bans ----------
function BanBoard({ bans }) {
  if (!bans?.length) return null;
  const top = bans.slice(0, 5);
  const rest = bans.slice(5, 8);
  return (
    <section style={{ marginBottom: 40 }}>
      <SecHead title="Bans recommandés" meta="score = volume × winrate + récence + maîtrise + picks Clash" />
      <div className="g-5 stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 14 }}>
        {top.map((b, i) => {
          const info = champInfo(b);
          return (
            <div key={info.id} className="card card-flush" style={{ overflow: 'hidden' }}>
              <div style={{ position: 'relative', aspectRatio: '3/2', overflow: 'hidden' }}>
                <img src={champLoading(info.id)} alt={info.label} loading="lazy"
                     onError={(e) => { e.target.src = champSquare(info.id); }}
                     style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}/>
                <span style={{ position: 'absolute', top: 8, left: 8, background: 'var(--loss)', color: '#fff',
                               fontWeight: 800, fontSize: 12, padding: '2px 8px', borderRadius: 8 }}>
                  Ban #{i + 1}
                </span>
              </div>
              <div style={{ padding: 14 }}>
                <div className="row-bw">
                  <span style={{ fontWeight: 800, fontSize: 15 }}>{info.label}</span>
                  <span className="mono muted" style={{ fontSize: 11 }}>{b.score} pts</span>
                </div>
                {b.players.slice(0, 2).map((pl) => (
                  <div key={pl.riotId} className="muted" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>
                    <strong style={{ color: 'var(--ink-2)' }}>{pl.riotId.split('#')[0]}</strong> — {pl.reasons.slice(0, 2).join(' · ')}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {rest.length > 0 && (
        <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          Aussi à surveiller : {rest.map((b) => champInfo(b).label).join(', ')}.
        </p>
      )}
    </section>
  );
}

export { ClashScreen };
